import { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { Banknote, BarChart3, Bot, CalendarDays, ChefHat, LayoutGrid, LifeBuoy, LogOut, MessageSquare, Phone, Shield, Users } from "lucide-react";
import { useAuth } from "../lib/auth";
import { SupportModal } from "./SupportModal";
import { fetchChatStats } from "../modules/chat/chatApi";
import { fetchVoicemailStats } from "../modules/voice/voiceApi";
import { fetchSmsStats } from "../modules/sms/smsApi";
import { useEventsIndicators } from "../modules/events/useEventsIndicators";

const ICONS: Record<string, typeof MessageSquare> = {
  sms: MessageSquare,
  voice: Phone,
  chat: Bot,
  food: ChefHat,
  members: Users,
  tips: Banknote,
  events: CalendarDays,
  reports: BarChart3,
  admin: Shield,
};

const LABELS: Record<string, string> = {
  sms: "SMS",
  voice: "Voice",
  chat: "Chatbot",
  food: "Food Cost",
  members: "Membership",
  tips: "Tips",
  events: "Events",
  reports: "Reports",
  admin: "Admin",
};

// Admins see every module in this fixed order. Non-admins see whatever's
// in `me.modules` (never `reports` / `admin` — those are admin-only by
// virtue of never being granted via `hq_user_modules`).
const ADMIN_MODULE_ORDER = [
  "sms",
  "voice",
  "chat",
  "food",
  "members",
  "tips",
  "events",
  "reports",
  "admin",
];

export function Sidebar() {
  const { me, signOut, hasModule } = useAuth();
  const [supportOpen, setSupportOpen] = useState(false);
  const [unansweredCount, setUnansweredCount] = useState(0);
  const [unheardVoicemails, setUnheardVoicemails] = useState(0);
  const [unreadSms, setUnreadSms] = useState(0);
  const { newInquiries, scheduledCalls } = useEventsIndicators(!!me && hasModule("events"));

  useEffect(() => {
    if (!me || !hasModule("chat")) return;
    let cancelled = false;
    const poll = () => {
      fetchChatStats()
        .then((s) => { if (!cancelled) setUnansweredCount(s.unansweredMessages); })
        .catch(() => {});
    };
    poll();
    const id = setInterval(poll, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [me, hasModule]);

  useEffect(() => {
    if (!me || !hasModule("voice")) return;
    let cancelled = false;
    const poll = () => {
      fetchVoicemailStats()
        .then((s) => { if (!cancelled) setUnheardVoicemails(s.unheard); })
        .catch(() => {});
    };
    poll();
    const id = setInterval(poll, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [me, hasModule]);

  useEffect(() => {
    if (!me || !hasModule("sms")) return;
    let cancelled = false;
    const poll = () => {
      fetchSmsStats()
        .then((s) => { if (!cancelled) setUnreadSms(s.unread); })
        .catch(() => {});
    };
    poll();
    const id = setInterval(poll, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [me, hasModule]);

  if (!me) return null;

  const modules = me.is_admin ? ADMIN_MODULE_ORDER : me.modules;

  return (
    <aside className="flex w-14 flex-col border-r border-tribal-border bg-tribal-surface sm:w-56">
      <div className="flex items-center justify-center gap-2 border-b border-tribal-border px-2 py-4 sm:justify-start sm:px-4">
        <div className="text-2xl leading-none" aria-hidden>🪓</div>
        <div className="hidden font-semibold tracking-tight sm:block">
          <span className="text-tribal-red">Tribal Axe</span> HQ
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-3" aria-label="Modules">
        <NavLink
          to="/"
          end
          title="Home"
          className={({ isActive }) =>
            `mx-1 mb-1 flex items-center justify-center gap-2 rounded-md px-2 py-2 text-sm transition-colors sm:mx-2 sm:justify-start sm:px-3 ${
              isActive ? "bg-tribal-red/15 text-white" : "text-tribal-muted hover:bg-white/5 hover:text-white"
            }`
          }
        >
          <LayoutGrid className="h-5 w-5 shrink-0 sm:h-4 sm:w-4" aria-hidden />
          <span className="hidden sm:inline">Home</span>
        </NavLink>

        <div className="mt-3 hidden px-4 pb-1 text-[10px] font-semibold uppercase tracking-wider text-tribal-muted sm:block">
          Modules
        </div>
        <div className="mx-2 my-2 h-px bg-tribal-border sm:hidden" aria-hidden />

        {modules.map((slug) => {
          const Icon = ICONS[slug] ?? LayoutGrid;
          const label = LABELS[slug] ?? slug;
          const badge =
            slug === "sms" && unreadSms > 0 ? unreadSms
            : slug === "voice" && unheardVoicemails > 0 ? unheardVoicemails
            : slug === "chat" && unansweredCount > 0 ? unansweredCount
            : slug === "events" && newInquiries > 0 ? newInquiries
            : 0;
          const showCallIcon = slug === "events" && scheduledCalls > 0;
          return (
            <NavLink
              key={slug}
              to={`/${slug}`}
              title={label}
              className={({ isActive }) =>
                `mx-1 mb-1 flex items-center justify-center gap-2 rounded-md px-2 py-2 text-sm transition-colors sm:mx-2 sm:justify-start sm:px-3 ${
                  isActive ? "bg-tribal-red/15 text-white" : "text-tribal-muted hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <div className="relative">
                <Icon className="h-5 w-5 shrink-0 sm:h-4 sm:w-4" aria-hidden />
                {badge > 0 && (
                  <span className="absolute -right-1.5 -top-1.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white sm:h-4 sm:w-4 sm:text-[10px]">
                    {badge > 9 ? "9+" : badge}
                  </span>
                )}
              </div>
              <span className="hidden sm:inline">{label}</span>
              {showCallIcon && (
                <Phone
                  className="ml-auto hidden h-3.5 w-3.5 shrink-0 text-blue-300 sm:block"
                  aria-label={`${scheduledCalls} scheduled call${scheduledCalls === 1 ? "" : "s"}`}
                >
                  <title>{`${scheduledCalls} scheduled call${scheduledCalls === 1 ? "" : "s"}`}</title>
                </Phone>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="px-2 pb-2 sm:px-3">
        <button
          type="button"
          onClick={() => setSupportOpen(true)}
          title="Request Support"
          className="mx-1 flex w-[calc(100%-8px)] items-center justify-center gap-2 rounded-md px-2 py-2 text-sm text-tribal-muted transition-colors hover:bg-white/5 hover:text-white sm:mx-0 sm:w-full sm:justify-start sm:px-3"
        >
          <LifeBuoy className="h-5 w-5 shrink-0 sm:h-4 sm:w-4" aria-hidden />
          <span className="hidden sm:inline">Request Support</span>
        </button>
      </div>

      {supportOpen && <SupportModal onClose={() => setSupportOpen(false)} />}

      <div className="border-t border-tribal-border p-2 sm:p-3">
        <div className="flex flex-col items-center gap-2 sm:flex-row">
          {me.avatar_url ? (
            <img
              src={me.avatar_url}
              alt=""
              className="h-7 w-7 shrink-0 rounded-full border border-tribal-border"
            />
          ) : (
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-tribal-red text-xs font-semibold">
              {me.email[0].toUpperCase()}
            </div>
          )}
          <div className="hidden min-w-0 flex-1 sm:block">
            <div className="truncate text-xs font-medium">{me.display_name ?? me.email}</div>
            <div className="truncate text-[10px] text-tribal-muted">{me.email}</div>
          </div>
          <button
            type="button"
            onClick={signOut}
            aria-label="Sign out"
            title="Sign out"
            className="rounded p-1.5 text-tribal-muted transition-colors hover:bg-white/5 hover:text-white"
          >
            <LogOut className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>
    </aside>
  );
}
